import {
    BookingListItem,
    BookingStatusColor,
    BookingStatusKey,
} from '../../types/booking/bookingListTypes';
import {
    AgeSegmentRoomState,
    BookingHotelImageState,
    BookingState,
} from '../../types/booking/BookingTypes';
import { ISupplierBooking } from '../../types/entities/ISupplierBooking';
import {
    OTA_REFERENCE_TABLE_CODE_KEY,
    PAYMENT_ACCOMPLISHED_KEY,
} from '../../utils/bookings/bookingUtils';

export const adaptBookingsListToState = (
    ddbbBooking: ISupplierBooking[]
): BookingListItem[] => {
    return ddbbBooking.map(adaptListBookingToState);
};

export const adaptListBookingToState = (
    ddbbBooking: ISupplierBooking
): BookingListItem => {
    const statusKey = ddbbBooking.SupplierBookingStatus
        ?.SupplierBookingStatusType?.Key as BookingStatusKey;
    return {
        Id: ddbbBooking.Id,
        IdStatus:
            ddbbBooking.SupplierBookingStatus?.IdSupplierBookingStatusType,
        Status:
            ddbbBooking.SupplierBookingStatus?.SupplierBookingStatusType
                ?.LocalizedCaption ||
            ddbbBooking.SupplierBookingStatus?.SupplierBookingStatusType
                ?.Caption ||
            ddbbBooking.SupplierBookingStatus?.SupplierBookingStatusType
                ?.Name ||
            '',
        StatusKey: statusKey,
        StatusColor: BookingStatusColor[statusKey],
        BookingCode: ddbbBooking.BookingCode,
        IdSupplierHotel: ddbbBooking.IdSupplierHotel,
        SupplierHotel: ddbbBooking.SupplierHotel?.Name || '',
        CheckInDate: ddbbBooking.CheckInDate,
        CheckOutDate: ddbbBooking.CheckOutDate,
        BookingDate: ddbbBooking.BookingDate,
    };
};

export const adaptDDBBBookingToState = (
    booking: ISupplierBooking
): BookingState => {
    const hotelImages: BookingHotelImageState[] =
        booking?.SupplierHotel?.SupplierHotelImages?.filter(
            img => img?.Published
        )
            .sort((a, b) =>
                a?.Feature === b?.Feature ? 0 : a?.Feature ? -1 : 1
            ) // 2️⃣ feature primero
            .slice(0, 4)
            .map(image => ({
                id: image?.Id || '',
                caption: image?.Caption || '',
                published: image?.Published || false,
                feature: image?.Feature || false,
                virtualPath: image?.VirtualPath || '',
            })) || [];

    while (hotelImages.length < 4) {
        hotelImages.push(null);
    }
    const payment = booking?.Payments?.filter(
        p =>
            p?.SupplierBooking?.BookingCode === booking?.BookingCode &&
            p?.ZoeBookingPaymentStatus?.ZoeBookingPaymentStatusType?.Key ===
                PAYMENT_ACCOMPLISHED_KEY
    )?.[0];
    return {
        status:
            booking?.SupplierBookingStatus?.SupplierBookingStatusType
                ?.LocalizedCaption ||
            booking?.SupplierBookingStatus?.SupplierBookingStatusType
                ?.Caption ||
            booking?.SupplierBookingStatus?.SupplierBookingStatusType?.Name ||
            '',
        statusColor:
            booking?.SupplierBookingStatus?.SupplierBookingStatusType?.Color ||
            '',
        isCancelled: booking.Cancelled,
        idHotel: booking?.SupplierHotel?.Id,
        hotel: booking?.SupplierHotel?.Name || '',
        hotelImages,
        address: booking?.SupplierHotel?.Hotel?.Address || '',
        hotelRating:
            booking?.SupplierHotel?.Hotel?.HotelCategory?.RatingNumber || 0,
        hotelCategory:
            booking?.SupplierHotel?.Hotel?.HotelCategory?.LocalizedCaption ||
            booking?.SupplierHotel?.Hotel?.HotelCategory?.Caption ||
            booking?.SupplierHotel?.Hotel?.HotelCategory?.LocalizedCaption ||
            '',
        bookingCode: booking?.BookingCode || '',
        hotelRefecerenceCode:
            booking?.ReservationIDs?.find(
                item =>
                    item?.OTAReferenceTableCode?.Key ===
                    OTA_REFERENCE_TABLE_CODE_KEY
            )?.ResID_Value || '',
        checkIn: booking?.CheckInDate || null,
        checkOut: booking?.CheckOutDate || null,
        bookingDate: booking?.BookingDate || null,
        nights: booking?.Nights || 0,
        paxes: booking?.Paxes || 0,
        totalAmount: booking?.TotalAmount || 0,
        paymentId: parseInt(booking?.SupplierBookingPaymentInfo?.Id) || 0,
        currency: booking?.Currency?.Key || '',
        customerName: booking?.ZoeBooking?.EndCustomer?.FullName || '',
        notes: booking?.Remarks || '',
        paymentAmount: payment?.Amount || 0,
        paymentCurrency: payment?.Currency?.Name || '',
        cancellationPolicies:
            booking?.SupplierBookingCancellationPolicy?.DecodedPenalties.map(
                (pen, index) => ({ text: pen, id: index + 1 })
            ),
        rooms: booking?.Rooms?.map(room => ({
            id: room?.Id || '',
            roomType: room?.SupplierHotelRoomType?.Name || '',
            roomMappingCode: room?.SupplierHotelRoomType?.MappingCode || '',
            mealPlan: room?.SupplierHotelMealPlan?.Name || '',
            mealPlanMappingCode: room?.SupplierHotelMealPlan?.MappingCode || '',
            ratePlan: room?.SupplierHotelRatePlan?.Name || '',
            ratePlanMappingCode: room?.SupplierHotelRatePlan?.MappingCode || '',
            rates: room?.Rates?.map(rate => ({
                id: rate?.Id || '',
                amount: rate?.AmountAfterTax || 0,
            })),
            guests:
                room?.Paxes?.map(pax => ({
                    name: pax?.Name || '',
                    surname: pax?.Surname || '',
                    age: pax?.Age || 0,
                    ageSegment: pax.IsBaby
                        ? AgeSegmentRoomState.BABY
                        : pax?.IsChild
                        ? AgeSegmentRoomState.CHILD
                        : AgeSegmentRoomState.ADULT,
                })) || [],
            descriptions:
                room?.SupplierHotelRoomType.SupplierHotelRoomTypeDescriptions?.filter(
                    description =>
                        description.DescriptionType.Key === 'LONG' &&
                        description.Description
                ).map(description => ({
                    id: description.Id,
                    idLanguage: description.IdContentLanguage,
                    languageCode: description.ContentLanguage.CultureId,
                    language:
                        description.ContentLanguage?.LocalizedCaption ||
                        description.ContentLanguage?.Caption ||
                        description.ContentLanguage?.Name ||
                        '',
                    descriptionContent: description.Description,
                })),
            prices:
                room?.Rates?.map((rate, ind) => ({
                    id: rate?.Id || '',
                    amount: rate?.AmountAfterTax || 0,
                    night: ind + 1,
                })) || [],
            taxes:
                room?.Taxes.map(tax => {
                    return {
                        id: tax.Id,
                        name: tax?.SupplierTax.Name || '',
                        description: tax?.SupplierTax.Description || '',
                        amount: tax?.Amount || 0,
                        totalAmount: tax?.Amount || 0,
                        currencyKey: tax?.Currency?.Key || '',
                        supplierSupplement: tax?.SupplierTax?.Name || '',
                        onlyAdults: tax?.SupplierTax?.OnlyAdults || false,
                        onlyChildren: tax?.SupplierTax?.OnlyChildren || false,
                        supplierTaxCalculationRuleType:
                            tax?.SupplierTax.SupplierTaxCalculationRuleType
                                ?.LocalizedCaption ||
                            tax?.SupplierTax.SupplierTaxCalculationRuleType
                                ?.Caption ||
                            tax?.SupplierTax.SupplierTaxCalculationRuleType
                                ?.Name ||
                            '',
                        supplierTaxCalculationRuleTypeKey:
                            tax?.SupplierTax.SupplierTaxCalculationRuleType
                                ?.Key || '',
                        supplierTaxCalculationType:
                            tax?.SupplierTax.SupplierTaxCalculationType
                                ?.LocalizedCaption ||
                            tax?.SupplierTax.SupplierTaxCalculationType
                                ?.Caption ||
                            tax?.SupplierTax.SupplierTaxCalculationType?.Name ||
                            '',
                        supplierTaxCalculationTypeKey:
                            tax?.SupplierTax.SupplierTaxCalculationType?.Key ||
                            '',
                    };
                }) || [],
            supplements:
                room?.Supplements.map(supplement => {
                    return {
                        id: supplement.Id,
                        name: supplement?.SupplierSupplement.Name || '',
                        description:
                            supplement?.SupplierSupplement.Description || '',
                        amount: supplement?.Amount || 0,
                        currencyKey: supplement?.Currency?.Key || '',
                        supplierSupplement:
                            supplement?.SupplierSupplement?.Name || '',
                        onlyAdults:
                            supplement?.SupplierSupplement?.OnlyAdults || false,
                        onlyChildren:
                            supplement?.SupplierSupplement?.OnlyChildren ||
                            false,
                        supplierTaxCalculationRuleType:
                            supplement?.SupplierSupplement
                                .SupplierTaxCalculationRuleType
                                ?.LocalizedCaption ||
                            supplement?.SupplierSupplement
                                .SupplierTaxCalculationRuleType?.Caption ||
                            supplement?.SupplierSupplement
                                .SupplierTaxCalculationRuleType?.Name ||
                            '',
                        supplierTaxCalculationRuleTypeKey:
                            supplement?.SupplierSupplement
                                .SupplierTaxCalculationRuleType?.Key || '',
                        supplierTaxCalculationType:
                            supplement?.SupplierSupplement
                                .SupplierTaxCalculationType?.LocalizedCaption ||
                            supplement?.SupplierSupplement
                                .SupplierTaxCalculationType?.Caption ||
                            supplement?.SupplierSupplement
                                .SupplierTaxCalculationType?.Name ||
                            '',
                        supplierTaxCalculationTypeKey:
                            supplement?.SupplierSupplement
                                .SupplierTaxCalculationType?.Key || '',
                    };
                }) || [],
        })),
    };
};
